import React, {FC, PropsWithChildren} from 'react';

import {extractInitials} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import apiAvatar16 from '../../../assets/images/apiAvatar16.png';
import apiAvatar30 from '../../../assets/images/apiAvatar30.png';
import ruleAvatar16 from '../../../assets/images/ruleAvatar16.png';
import ruleAvatar30 from '../../../assets/images/ruleAvatar30.png';
import {
  buildTeammateDisplayName,
  isApiTeammate,
  isCsatTeammate,
  isRuleTeammate,
  isVisitorTeammate,
} from '../../../helpers/modelHelpers';
import {TeammateModel} from '../../../models/api/teammateModel';
import {AvatarProps} from './avatar';
import {ColorTeammateAvatar} from './colorTeammateAvatar';
import {ImageTeammateAvatar} from './imageTeammateAvatar';
import {StaticTeammateAvatar} from './staticTeammateAvatar';
import {VisitorTeammateAvatar} from './visitorTeammateAvatar';

/*
 * Props.
 */

export interface TeammateAvatarProps extends AvatarProps {
  /** The {@link ITeammate} to display. */
  teammate: TeammateModel;
  /** Whether the avatar should be desaturated and transparent. */
  isFaded?: boolean;
  /** Whether this avatar is on a darker background. */
  isSelected?: boolean;
  /** Whether this avatar changes on parent hover. */
  supportsHover?: boolean;
  /** Whether we should display a darker hover color. */
  hasDarkerHover?: boolean;
}

/*
 * Component.
 */

/** Display the avatar of the specified {@link ITeammate}. */
export const TeammateAvatar: FC<TeammateAvatarProps> = (props) => {
  const teammateName = buildTeammateDisplayName(props.teammate);
  return (
    <>
      <VisuallyHidden>{teammateName},</VisuallyHidden>
      {renderTeammateAvatar(props, teammateName)}
    </>
  );
};

function renderTeammateAvatar(props: PropsWithChildren<TeammateAvatarProps>, teammateName: string) {
  const {
    teammate,
    isFaded,
    isSelected,
    supportsHover,
    hasDarkerHover,
    children,
    size,
    borderColor,
    borderHoverColor,
    className,
  } = props;

  const {avatarUrl} = teammate;

  // If we have a custom avatar, render the image.
  if (avatarUrl) {
    return (
      <ImageTeammateAvatar
        avatarUrl={avatarUrl}
        isFaded={isFaded}
        supportsHover={supportsHover}
        hasDarkerHover={hasDarkerHover}
        size={size}
        borderColor={borderColor}
        borderHoverColor={borderHoverColor}
        className={className}
      >
        {children}
      </ImageTeammateAvatar>
    );
  }

  // If this is a visitor, it has a custom appearance.
  const initials = extractInitials(teammateName);
  if (isVisitorTeammate(teammate)) {
    return (
      <VisitorTeammateAvatar
        initials={initials}
        isSelected={isSelected}
        size={size}
        borderColor={borderColor}
        borderHoverColor={borderHoverColor}
        className={className}
      >
        {children}
      </VisitorTeammateAvatar>
    );
  }

  // TODO: remove this when the backend actually includes the csat URL on the teammate
  if (isCsatTeammate(teammate)) {
    return (
      <ImageTeammateAvatar
        avatarUrl="https://survey.frontapp.com/assets/csat.png"
        isFaded={isFaded}
        supportsHover={supportsHover}
        hasDarkerHover={hasDarkerHover}
        size={size}
        borderColor={borderColor}
        borderHoverColor={borderHoverColor}
        className={className}
      >
        {children}
      </ImageTeammateAvatar>
    );
  }

  if (isRuleTeammate(teammate)) {
    return (
      <StaticTeammateAvatar
        avatar16={ruleAvatar16}
        avatar30={ruleAvatar30}
        isFaded={isFaded}
        supportsHover={supportsHover}
        hasDarkerHover={hasDarkerHover}
        size={size}
        borderColor={borderColor}
        borderHoverColor={borderHoverColor}
        className={className}
      >
        {children}
      </StaticTeammateAvatar>
    );
  }

  if (isApiTeammate(teammate)) {
    return (
      <StaticTeammateAvatar
        avatar16={apiAvatar16}
        avatar30={apiAvatar30}
        isFaded={isFaded}
        supportsHover={supportsHover}
        hasDarkerHover={hasDarkerHover}
        size={size}
        borderColor={borderColor}
        borderHoverColor={borderHoverColor}
        className={className}
      >
        {children}
      </StaticTeammateAvatar>
    );
  }

  // Otherwise, render the color avatar.
  return (
    <ColorTeammateAvatar
      initials={initials}
      color={props.teammate.color}
      isFaded={isFaded}
      supportsHover={supportsHover}
      hasDarkerHover={hasDarkerHover}
      size={size}
      borderColor={borderColor}
      borderHoverColor={borderHoverColor}
      className={className}
    >
      {children}
    </ColorTeammateAvatar>
  );
}
